import { useEffect, useState } from "react";
import { TableNode, HiearchicalTable, HiearchicalTableColumn} from "../../../components/molecules/HiearchicalTable";
import { ServiceActionModal } from "./serviceActionModal";
import { Change, ChangeChip, ChangeIcon, changeValues, getDisplayText } from "./changeChip";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { MoreVert} from "@mui/icons-material";
import { FetchFromBackoffice } from "../../../utils/request";

export interface BOMItem extends TableNode{
  partNumber: string,
  name: string, 
  quantity: number,
  unit: string,
  change?: Change,
  description?: string
}

export type ModalState  = {
  isOpen: boolean, 
  type: Change | null,
  currentItem: BOMItem | undefined,
  parentItem?: BOMItem | undefined
}

export const AssetHistory = ({serialId}:{serialId?: string}) => {

  const [parts, setParts] = useState<BOMItem>()

  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    type: null,
    currentItem: undefined,
    parentItem: undefined
  });


  const countChangesInRow = (row: BOMItem) : number =>{
    if(!row) return 0

    let tempCount = 0
    if(row.change){
      tempCount = tempCount + 1
    }
    return tempCount + row.children.flatMap((child) => countChangesInRow(child as BOMItem)).reduce((acc, curr) => acc + curr, 0)

  }

  const columnNames : HiearchicalTableColumn<BOMItem>[] = [
    {
      displayName: "Part number",
      id: "partNumber",
      render: (r,level,_) => renderRow(r.partNumber, level, r.change)
    },
    {
      displayName: "Name",
      id: "name",
      render: (r,level,_) => renderRow(r.name, level, r.change)
    },
    {
      displayName: "Quantity",
      id: "quantity",
      render: (r,level,_) =>  renderRow(`${r.quantity} ${r.unit}`, level, r.change)
    },
    {
      displayName: "Change",
      id: "change",
      render: (r: BOMItem,_) => {
        const changeCount = countChangesInRow(r);
        if(r.change){
          return (
            <Box sx={{display:"flex", flexDirection:"column", gap: "3px"}}>
              <ChangeChip changeValue={r.change}/>
              {
                changeCount -1 > 0 && (
                  <Typography variant="body2">({changeCount-1} {changeCount - 1 === 1 ? "change" : "changes"} inside)</Typography>
                )
              }
            </Box>
          )
        } 

        if (changeCount > 0){
          return <Typography>{changeCount} {changeCount === 1 ? "change" : "changes"} inside</Typography>
        }  
        return null
      }
    },
    {
      displayName: "",
      id: "action", 
      render: (r, _, p) => r.change !== "Removed" && <ActionMenu row={r} parentRow={p} setModalState={setModalState}/>
    }
  ] 

  const renderRow = (text: string, level: number, change?: Change) : React.ReactNode => {
    if (change === "Removed") {
      return <Typography variant="body2" sx={{ color: `rgba(110, 110, 110, ${1 - (0.02 * level)})` }}>{text}</Typography>;
    }
    return <Typography variant="body2">{text}</Typography>;
  };


  const fetchParts = async() => {
    if (serialId) {
      try {
        const parts = await FetchFromBackoffice<BOMItem>(`machines/${serialId}/hardware`)
        setParts(parts);
      } catch (error) {
        console.error("Error fetching parts:", error);
      }
    }
  }

  useEffect(() => {
    fetchParts()
  }, [serialId])


  return (
    <>
      {modalState.currentItem && modalState.isOpen && modalState.type && serialId && parts &&
        <ServiceActionModal
          open={modalState.isOpen}
          closeModal={() => setModalState((prev) => ({
              ...prev,
              isOpen: false,
            }))}
          onSaved={(newParts) => setParts(newParts)}
          modalType={modalState.type}
          item={modalState.currentItem}
          parentItem={modalState.parentItem}
          serialId = {serialId}
          parts={parts}
        />
      }
      {
        parts ? (
          <HiearchicalTable
            columnNames={columnNames}
            rows={parts}
          />
        ) :
        (<Typography>No BOM exists for this hub</Typography>)
      }
    </>
  )

}

export const ActionMenu = ({row, parentRow, setModalState}: {row: BOMItem, parentRow?: BOMItem, setModalState: (modalState: ModalState) => void}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const possibleActions: Change[] = parentRow ? changeValues : ["Added"]
  return (
    <Box>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls={open ? 'long-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVert />
      </IconButton>
      <Menu
        open={open}
        onClose={handleClose}
        id="long-menu"
        anchorEl={anchorEl}
      >
        {possibleActions.map((option) => (
          <MenuItem sx={{gap: "5px"}} key={option} onClick={() => {
            handleClose()
            setModalState({
              isOpen: true,
              type: option,
              currentItem: row,
              parentItem: parentRow
            })
          }}>
            <ChangeIcon changeValue={option}/>
            {getDisplayText(option)}
          </MenuItem>
        ))}
      </Menu>
    </Box>

  )
}